// @ts-check
/** @typedef {import('../types/api').SiteSetting} SiteSetting */

import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Film,
  Image,
  RefreshCw,
  Save,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AnnouncementsManager from '../components/AnnouncementsManager';
import CraftedConsciouslyImagesManager from '../components/CraftedConsciouslyImagesManager';
import FeaturedProductsManager from '../components/FeaturedProductsManager';
import FileUpload from '../components/FileUpload';
import HeroSlidesManager from '../components/HeroSlidesManager';
import LoadingScreen from '../components/LoadingScreen';
import MediaJobPicker from '../components/MediaJobPicker';
import { DESKTOP_RATIOS, MOBILE_RATIOS, OurStoryPreview, RatioPicker } from '../components/OurStoryRatio';
import SectionManager from '../components/SectionManager';
import ShopTilesManager from '../components/ShopTilesManager';
import TrustPillarsManager from '../components/TrustPillarsManager';
import { useToast } from '../context/ToastContext';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import adminSiteSettingsService from '../services/adminSiteSettingsService';
import api from '../utils/apiClient';
import { getVideoThumbnailUrl } from '../utils/mediaThumbnail';

// Settings that the admin page manages — with defaults for when they don't exist in DB yet
const MANAGED_DEFAULTS = {
  // Homepage Our Story CTA
  our_story_heading: 'Our Story',
  our_story_button_text: 'Discover More',
  our_story_button_link: '/about',
  our_story_media_url: '',
  our_story_media_type: 'image',
  // Video aspect ratio per breakpoint ("16/9"); '' = default 16:9
  our_story_ratio_desktop: '',
  our_story_ratio_mobile: '',
  // Product carousel titles
  new_collection_title: 'New Collection',
  featured_products_1_title: '',
  featured_products_2_title: "Après l'Été",
  // Header menu toggles
  header_nav_discover: 'true',
  header_nav_dresses: 'true',
  header_nav_sets_separates: 'true',
  header_nav_our_story: 'false',
  header_mega_by_type: 'true',
  header_mega_by_occasion: 'true',
  header_mega_by_mood: 'false',
  header_mega_by_place: 'false',
  header_mega_by_collection: 'true',
  // Right-side icons
  header_icon_search: 'true',
  header_icon_profile: 'true',
  header_icon_wishlist: 'true',
  header_icon_cart: 'true',
  // Header menu item selections (RTW mega menu)
  header_menu_selected_types: '',
  header_menu_selected_occasions: '',
  header_menu_selected_moods: '',
  header_menu_selected_places: '',
  header_menu_selected_collections: '',
  // Discover dropdown selections
  header_discover_selected_phantom: '',
  header_discover_selected_collections: '',
  // Dresses dropdown selections
  header_dresses_selected_features: '',
  // Collection Welcome section
  collection_welcome_heading: '',
  collection_welcome_description: '',
  collection_welcome_cta_text: 'EXPLORE THE COLLECTION',
  collection_welcome_cta_link: '/ready-to-wear',
  // About page sections
  about_section_1_image: '',
  about_section_1_mobile_image: '',
  // Phantom collection hero images
  'phantom_new-arrivals_hero_image': '',
  'phantom_new-arrivals_mobile_hero_image': '',
  'phantom_coming-soon_hero_image': '',
  'phantom_coming-soon_mobile_hero_image': '',
  'phantom_core-collection_hero_image': '',
  'phantom_core-collection_mobile_hero_image': '',
  phantom_bestsellers_hero_image: '',
  phantom_bestsellers_mobile_hero_image: '',
  about_section_2_title: 'Our Story',
  about_section_2_content: '',
  about_section_3_title: '',
  about_section_3_subtitle: '',
  about_section_3_content: '',
  about_section_3_image: '',
  about_section_4_title: '',
  about_section_4_subtitle: '',
  about_section_4_content: '',
  about_section_4_image: '',
  about_section_5_title: '',
  about_section_5_content: '',
  // Instagram Feed grid
  instagram_grid_pattern: 'alternating',
  // Showcase (Spotted + UGC)
  showcase_heading: 'Seen in KAAY',
  showcase_subheading: '',
  showcase_home_min_count: '3',
  showcase_home_max_items: '8',
  showcase_pdp_min_count: '1',
  // Newsletter popup
  newsletter_enabled: 'false',
  newsletter_image: '',
  newsletter_heading: '',
  newsletter_subtext: '',
  newsletter_cta_text: 'Subscribe',
};

const TABS = [
  { key: 'header', label: 'Header' },
  { key: 'home', label: 'Home Page' },
  { key: 'about', label: 'About Page' },
  { key: 'pdp', label: 'Product Page' },
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'payment', label: 'Payment' },
  { key: 'size_recommender', label: 'Size Recommender' },
  { key: 'currency', label: 'Currency' },
];

// Static phantom collection options for Discover checkbox group
const PHANTOM_COLLECTION_OPTIONS = [
  { id: 'new-arrivals', name: 'New In' },
  { id: 'coming-soon', name: 'Coming Soon' },
  { id: 'core-collection', name: 'Core Collection' },
  { id: 'bestsellers', name: 'Bestsellers' },
];

// Homepage sections in default order — mirrors frontend-next's HomePageClient.tsx
// SECTION_KEYS. Passed to the generic SectionManager, which handles both reordering
// and show/hide for this list; keep in sync if a section is added/removed there.
const HOME_SECTIONS = [
  { key: 'hero', label: 'Hero Section (carousel / image / video)' },
  { key: 'collection_welcome', label: 'Collection Welcome Text' },
  { key: 'shop_tiles', label: 'Shop By Tiles (3-tile carousel)' },
  { key: 'new_collection', label: 'New Collection (horizontal scroll)' },
  { key: 'collection_showcase', label: 'Collection Showcase (grid)' },
  { key: 'featured_products_1', label: 'Featured Products — Row 1' },
  { key: 'featured_products_2', label: "Featured Products — Row 2 (Apres l'Ete)" },
  { key: 'outfit_inspo', label: 'Outfit Inspiration Slider' },
  { key: 'why_kaay', label: 'Why KAAY Trust Section (also hides footer trust bar)' },
  { key: 'crafted_consciously_images', label: 'Crafted Consciously — Images' },
  { key: 'our_story', label: 'Our Story CTA Section' },
  { key: 'instagram', label: 'Instagram Feed' },
  { key: 'showcase', label: 'Showcase (Spotted + UGC)' },
];

// Map each tab to the key prefixes it owns
const TAB_KEY_PREFIXES = {
  header: ['site_logo_', 'header_', 'phantom_', 'announcement_bar_enabled'],
  home: [
    'section_',
    'our_story_',
    'featured_products_',
    'hero_',
    'collection_welcome_',
    'instagram_',
    'showcase_',
    'new_collection_',
  ],
  about: ['about_'],
  pdp: ['pdp_section_', 'pdp_'],
  newsletter: ['newsletter_'],
  shipping: ['shipping_'],
  size_recommender: ['size_recommendation_'],
};

const settingBelongsToTab = (key, tab) => TAB_KEY_PREFIXES[tab]?.some((prefix) => key.startsWith(prefix)) ?? false;

const SiteSettingsPage = () => {
  const toast = useToast();
  /** @type {[SiteSetting[], Function]} */
  const [settings, setSettings] = useState([]);
  // Snapshot of the last-loaded/last-saved values, used only to revert a tab's dirty
  // fields on Discard — never rendered directly.
  /** @type {[SiteSetting[], Function]} */
  const [originalSettings, setOriginalSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('header');
  const [dirtyKeys, setDirtyKeys] = useState(new Set());
  // Exactly one of these two Save affordances shakes at a time, whichever is actually
  // visible: the header button while it's still in view, the floating bar once it's
  // scrolled past (see the IntersectionObserver effect below).
  const [shakeTop, setShakeTop] = useState(false);
  const [shakeBar, setShakeBar] = useState(false);
  const topSaveBtnRef = useRef(null);
  const [topButtonVisible, setTopButtonVisible] = useState(true);

  useEffect(() => {
    // Not IntersectionObserver: its callbacks are throttled/delayed on background or
    // not-fully-visible tabs (real Chrome behavior, not just an automation quirk), so
    // it can lag well behind the actual scroll position. A capture-phase scroll
    // listener on window catches scroll events from ANY nested scrolling ancestor
    // (here, AdminLayout's <main overflow-y-auto>, which this page doesn't have a ref
    // to) and measures the button directly — no timing dependency.
    const checkVisibility = () => {
      const el = topSaveBtnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setTopButtonVisible(rect.bottom > 0 && rect.top < window.innerHeight);
    };
    checkVisibility();
    window.addEventListener('scroll', checkVisibility, true);
    window.addEventListener('resize', checkVisibility);
    return () => {
      window.removeEventListener('scroll', checkVisibility, true);
      window.removeEventListener('resize', checkVisibility);
    };
  }, []);

  // Taxonomy & collections data for header menu item selection
  const [taxonomyData, setTaxonomyData] = useState({
    types: [],
    events: [],
    moods: [],
    places: [],
    collections: [],
    features: [],
  });
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);

  const loadTaxonomyData = async () => {
    setTaxonomyLoading(true);
    try {
      const [types, events, moods, places, collections, features] = await Promise.all([
        api.get('/api/admin/taxonomy/product-types'),
        api.get('/api/admin/taxonomy/events'),
        api.get('/api/admin/taxonomy/moods'),
        api.get('/api/admin/taxonomy/places'),
        api.get('/api/admin/collections/'),
        api.get('/api/admin/taxonomy/features'),
      ]);
      setTaxonomyData({
        types: types || [],
        events: events || [],
        moods: moods || [],
        places: places || [],
        collections: collections || [],
        features: features || [],
      });
    } catch (err) {
      console.error('Failed to load taxonomy data:', err);
    } finally {
      setTaxonomyLoading(false);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSiteSettingsService.getAllSettings();

      // Ensure managed settings exist in local state
      const existingKeys = new Set(data.map((s) => s.key));
      const merged = [...data];
      Object.entries(MANAGED_DEFAULTS).forEach(([key, defaultValue]) => {
        if (!existingKeys.has(key)) {
          merged.push({ key, value: defaultValue, is_active: true, _isNew: true });
        }
      });

      setSettings(merged);
      setOriginalSettings(merged.map((s) => ({ ...s })));
      setDirtyKeys(new Set());
      setError('');
    } catch (err) {
      setError('Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadTaxonomyData();
  }, [loadSettings]);

  const hasChanges = [...dirtyKeys].some((key) => settingBelongsToTab(key, activeTab));

  // Cross-page guard: registers into the app-wide unsaved-changes registry
  // (AdminLayout mounts the single navigation-blocking guard), separate from
  // (and coexisting with) the in-page tab-switch guard above (handleTabClick
  // / the floating bar).
  useRegisterUnsavedChanges(hasChanges);

  const handleChange = (key, field, value) => {
    setSettings((prev) => prev.map((setting) => (setting.key === key ? { ...setting, [field]: value } : setting)));
    setDirtyKeys((prev) => new Set(prev).add(key));
  };

  const getSettingValue = (key) => {
    const s = settings.find((s) => s.key === key);
    return s?.value ?? MANAGED_DEFAULTS[key] ?? '';
  };

  const saveOrCreate = async (setting) => {
    const payload = { value: setting.value, is_active: setting.is_active };
    if (setting._isNew) {
      await adminSiteSettingsService.createSetting({ key: setting.key, ...payload });
      setting._isNew = false;
    } else {
      try {
        await adminSiteSettingsService.updateSetting(setting.key, payload);
      } catch (err) {
        if (err.message?.includes('not found')) {
          await adminSiteSettingsService.createSetting({ key: setting.key, ...payload });
        } else {
          throw err;
        }
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Only save settings that: belong to the active tab, were actually modified, and aren't hero slides
      const settingsToSave = settings.filter(
        (s) => dirtyKeys.has(s.key) && settingBelongsToTab(s.key, activeTab) && !s.key.startsWith('hero_'),
      );

      if (settingsToSave.length === 0) {
        setSuccess('No changes to save');
        setTimeout(() => setSuccess(''), 2000);
        setSaving(false);
        return;
      }

      for (const setting of settingsToSave) {
        await saveOrCreate(setting);
      }

      // Clear dirty state for saved keys
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        settingsToSave.forEach((s) => next.delete(s.key));
        return next;
      });

      // The saved values are now the new "last known good" state — Discard should
      // revert to these, not back to whatever was loaded at page-open time.
      const savedKeys = new Set(settingsToSave.map((s) => s.key));
      setOriginalSettings((prev) => {
        const next = prev.map((s) => (savedKeys.has(s.key) ? settings.find((cur) => cur.key === s.key) || s : s));
        settingsToSave.forEach((s) => {
          if (!next.some((o) => o.key === s.key)) next.push({ ...s });
        });
        return next;
      });

      setSuccess(`${settingsToSave.length} setting${settingsToSave.length > 1 ? 's' : ''} saved!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reverts the active tab's dirty fields back to their last-saved values — the "way
  // out" when the unsaved-changes bar blocks a tab switch (see handleTabClick).
  const handleDiscardTab = () => {
    const dirtyForTab = [...dirtyKeys].filter((key) => settingBelongsToTab(key, activeTab));
    setSettings((prev) =>
      prev.map((setting) => {
        if (!dirtyForTab.includes(setting.key)) return setting;
        const original = originalSettings.find((s) => s.key === setting.key);
        return original ? { ...original } : setting;
      }),
    );
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      dirtyForTab.forEach((key) => next.delete(key));
      return next;
    });
  };

  // Tab switching is blocked (with a shake nudge on whichever Save affordance is
  // currently on screen) while the CURRENT tab has unsaved changes — forces an
  // explicit Save or Discard before leaving.
  const handleTabClick = (key) => {
    if (key === activeTab) return;
    if (hasChanges) {
      if (topButtonVisible) {
        setShakeTop(true);
        setTimeout(() => setShakeTop(false), 500);
      } else {
        setShakeBar(true);
        setTimeout(() => setShakeBar(false), 500);
      }
      return;
    }
    setActiveTab(key);
  };

  // Section toggle handler (saves immediately)
  const handleToggleSection = async (setting) => {
    const newValue = setting.value === 'true' ? 'false' : 'true';
    handleChange(setting.key, 'value', newValue);
    try {
      await saveOrCreate({ ...setting, value: newValue });
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        next.delete(setting.key);
        return next;
      });
    } catch (err) {
      handleChange(setting.key, 'value', setting.value);
    }
  };

  if (loading) {
    return (
      <div>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Site Settings</h1>
        <button
          ref={topSaveBtnRef}
          onClick={handleSave}
          disabled={saving || !hasChanges}
          data-testid="save-settings-btn"
          className={`px-6 py-2 flex items-center gap-2 transition rounded-lg ${
            hasChanges
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-accent text-muted-foreground/70 cursor-not-allowed'
          } ${shakeTop ? 'animate-shake' : ''}`}
        >
          <Save size={20} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 text-green-700 dark:text-green-300 rounded-lg">
          {success}
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex border-b border-border mb-6 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            className={`flex-shrink-0 whitespace-nowrap px-4 md:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'header' && (
        <HeaderTab
          settings={settings}
          getSettingValue={getSettingValue}
          handleChange={handleChange}
          taxonomyData={taxonomyData}
          taxonomyLoading={taxonomyLoading}
          handleToggleSection={handleToggleSection}
          saveOrCreate={saveOrCreate}
          toast={toast}
        />
      )}

      {activeTab === 'home' && (
        <HomePageTab
          settings={settings}
          getSettingValue={getSettingValue}
          handleChange={handleChange}
          dirtyKeys={dirtyKeys}
        />
      )}

      {activeTab === 'about' && (
        <AboutPageTab
          getSettingValue={getSettingValue}
          handleChange={handleChange}
          saveOrCreate={saveOrCreate}
          settings={settings}
          toast={toast}
        />
      )}

      {activeTab === 'pdp' && <ProductPageTab settings={settings} handleToggleSection={handleToggleSection} />}

      {activeTab === 'newsletter' && <NewsletterTab getSettingValue={getSettingValue} handleChange={handleChange} />}

      {activeTab === 'shipping' && <ShippingTab toast={toast} />}

      {activeTab === 'payment' && <PaymentTab toast={toast} />}

      {activeTab === 'size_recommender' && <SizeRecommenderTab toast={toast} />}

      {activeTab === 'currency' && <CurrencySettingsTab toast={toast} />}

      {/* Floating unsaved-changes bar — truly pinned to the viewport (position: fixed,
          NOT sticky: a bottom-anchored sticky element only starts sticking once its
          natural position in the document flow nears the bottom of its container, so
          on a long page like this one it stayed invisible until you'd already scrolled
          almost all the way down — fixed has no such flow dependency). Only rendered
          while the header's own Save button has scrolled out of view, so the two
          affordances are never shown at once. Shaken by handleTabClick when the
          current tab is dirty and the user tries to switch away. */}
      {hasChanges && !topButtonVisible && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-xl ${shakeBar ? 'animate-shake' : ''}`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-4 bg-primary text-primary-foreground rounded-lg shadow-lg px-5 py-3">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
              You have unsaved changes
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <button
                onClick={handleDiscardTab}
                className="text-sm text-muted-foreground/50 hover:text-white underline"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 bg-card text-foreground text-sm font-medium rounded-lg hover:bg-accent transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ====================================================================
   TAB 1: HEADER
   ==================================================================== */
const HeaderTab = ({
  settings,
  getSettingValue,
  handleChange,
  taxonomyData,
  taxonomyLoading,
  handleToggleSection,
  saveOrCreate,
  toast,
}) => {
  // Branding
  const brandingKeys = ['site_logo_dark_url', 'site_logo_light_url'];
  const brandingSettings = brandingKeys.map((key) => settings.find((s) => s.key === key)).filter(Boolean);

  // Phantom collection hero upload — single active key at a time
  const [openPhantomUpload, setOpenPhantomUpload] = useState(null);

  return (
    <div className="space-y-8">
      {/* Announcement Bar */}
      <Section title="Announcement Bar" icon={null}>
        <div className="mb-4">
          <ToggleRow
            label="Show announcement bar"
            isEnabled={getSettingValue('announcement_bar_enabled') !== 'false'}
            onToggle={() =>
              handleChange(
                'announcement_bar_enabled',
                'value',
                getSettingValue('announcement_bar_enabled') === 'false' ? 'true' : 'false',
              )
            }
          />
          <p className="text-xs text-muted-foreground/70 mt-1">
            Master switch for the whole bar. Remember to hit "Save Changes" above after toggling. Turn individual
            announcements on/off below without affecting this switch.
          </p>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Add one or more announcements — each rotates through the scrolling bar with its own link. Leave the link blank
          if an announcement shouldn't be clickable.
        </p>
        <AnnouncementsManager />
      </Section>

      {/* Branding / Logos */}
      <Section title="Branding / Logos">
        <p className="text-sm text-muted-foreground mb-4">
          Upload two versions of your logo. Use transparent PNGs for best results. If no logos are uploaded, the site
          name "KAAY" is shown as text.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {brandingSettings.map((setting) => {
            const logoValue = setting.value;
            const isDark = setting.key.includes('dark');
            const label = isDark ? 'Dark Logo' : 'Light Logo';
            const description = isDark
              ? 'Used on white/light backgrounds (header after scroll)'
              : 'Used on hero overlay and dark backgrounds';
            const previewBg = isDark ? 'bg-white' : 'bg-gray-900';

            return (
              <LogoUploadCard
                key={setting.key}
                label={label}
                description={description}
                previewBg={previewBg}
                logoUrl={logoValue}
                onUpload={async (url) => {
                  handleChange(setting.key, 'value', url);
                  await saveOrCreate({ ...setting, value: url });
                  toast.success(`${label} updated`);
                }}
                onRemove={async () => {
                  handleChange(setting.key, 'value', '');
                  await saveOrCreate({ ...setting, value: '' });
                  toast.success(`${label} removed`);
                }}
              />
            );
          })}
        </div>
        {brandingSettings.length === 0 && (
          <p className="text-center text-muted-foreground/70 py-4">
            Logo settings will be available after next deploy.
          </p>
        )}
      </Section>

      {/* Navigation Toggles */}
      <Section title="Navigation Items">
        <div className="space-y-2">
          {[
            {
              key: 'header_nav_discover',
              label: 'Discover (mega menu dropdown — featured, collections, category, occasion, mood, place)',
            },
            { key: 'header_nav_dresses', label: 'Dresses (length dropdown — Mini/Midi/Maxi)' },
            { key: 'header_nav_sets_separates', label: 'Sets & Separates (type dropdown)' },
            { key: 'header_nav_our_story', label: 'Our Story' },
          ].map((item) => (
            <ToggleRow
              key={item.key}
              label={item.label}
              isEnabled={(settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true'}
              onToggle={() => {
                const current = (settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true';
                handleChange(item.key, 'value', current ? 'false' : 'true');
              }}
            />
          ))}
        </div>
      </Section>

      {/* Icon Toggles */}
      <Section title="Header Icons (right side)">
        <div className="space-y-2">
          {[
            { key: 'header_icon_search', label: 'Search icon' },
            { key: 'header_icon_profile', label: 'Profile / Account icon' },
            { key: 'header_icon_wishlist', label: 'Wishlist (heart) icon' },
            { key: 'header_icon_cart', label: 'Shopping bag / Cart icon' },
          ].map((item) => (
            <ToggleRow
              key={item.key}
              label={item.label}
              isEnabled={(settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true'}
              onToggle={() => {
                const current = (settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true';
                handleChange(item.key, 'value', current ? 'false' : 'true');
              }}
            />
          ))}
        </div>
      </Section>

      {/* Discover Dropdown Items */}
      <Section title="Discover Dropdown Items">
        <p className="text-sm text-muted-foreground mb-4">
          Choose which items appear in the Discover dropdown and drag them into the order you want.
        </p>
        {taxonomyLoading ? (
          <p className="text-sm text-muted-foreground/70 py-4">Loading...</p>
        ) : (
          <div className="space-y-4">
            <OrderedMenuItemSelector
              label="Featured (Phantom Collections)"
              items={PHANTOM_COLLECTION_OPTIONS}
              nameField="name"
              settingKey="header_discover_selected_phantom"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
            <OrderedMenuItemSelector
              label="Collections"
              items={taxonomyData.collections}
              nameField="name"
              settingKey="header_discover_selected_collections"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground/70 mt-4">
          Only checked items will appear. Leave all unchecked to hide the section entirely.
        </p>
      </Section>

      {/* Phantom Collection Hero Images */}
      <Section title="Phantom Collection Hero Images">
        <p className="text-sm text-muted-foreground mb-4">
          Upload a landscape (desktop) and portrait (mobile) hero image for each phantom collection page.
        </p>
        <div className="space-y-6">
          {PHANTOM_COLLECTION_OPTIONS.map((pc) => (
            <div key={pc.id}>
              <p className="text-sm font-medium mb-3">{pc.name}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ImageUploadField
                  label="Desktop (Landscape)"
                  imageUrl={getSettingValue(`phantom_${pc.id}_hero_image`)}
                  settingKey={`phantom_${pc.id}_hero_image`}
                  showUpload={openPhantomUpload === `phantom_${pc.id}_hero_image`}
                  setShowUpload={(v) => setOpenPhantomUpload(v ? `phantom_${pc.id}_hero_image` : null)}
                  onChange={handleChange}
                />
                <ImageUploadField
                  label="Mobile (Portrait)"
                  imageUrl={getSettingValue(`phantom_${pc.id}_mobile_hero_image`)}
                  settingKey={`phantom_${pc.id}_mobile_hero_image`}
                  showUpload={openPhantomUpload === `phantom_${pc.id}_mobile_hero_image`}
                  setShowUpload={(v) => setOpenPhantomUpload(v ? `phantom_${pc.id}_mobile_hero_image` : null)}
                  onChange={handleChange}
                />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Dresses Dropdown Items */}
      <Section title="Dresses Dropdown Items">
        <p className="text-sm text-muted-foreground mb-4">
          Choose which features appear in the Dresses hover menu and drag them into the order you want.
        </p>
        {taxonomyLoading ? (
          <p className="text-sm text-muted-foreground/70 py-4">Loading...</p>
        ) : (
          <OrderedMenuItemSelector
            label="Features"
            items={taxonomyData.features}
            nameField="name"
            settingKey="header_dresses_selected_features"
            getSettingValue={getSettingValue}
            onChange={handleChange}
            toast={toast}
          />
        )}
        <p className="text-xs text-muted-foreground/70 mt-4">
          Items appear in the header in the order shown above. Leave empty to hide the section entirely.
        </p>
      </Section>

      {/* Mega Menu Column Toggles */}
      <Section title="Mega Menu Columns (inside Ready to Wear)">
        <div className="space-y-2">
          {[
            { key: 'header_mega_by_type', label: 'By Type (Maxi, Mini, Midi, etc.)' },
            { key: 'header_mega_by_occasion', label: 'By Occasion (backed by Events)' },
            { key: 'header_mega_by_mood', label: 'By Mood' },
            { key: 'header_mega_by_place', label: 'By Place' },
            { key: 'header_mega_by_collection', label: 'By Collection' },
          ].map((item) => (
            <ToggleRow
              key={item.key}
              label={item.label}
              isEnabled={(settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true'}
              onToggle={() => {
                const current = (settings.find((s) => s.key === item.key)?.value ?? 'true') === 'true';
                handleChange(item.key, 'value', current ? 'false' : 'true');
              }}
            />
          ))}
        </div>
      </Section>

      {/* Menu Item Selection — ordered */}
      <Section title="Menu Items (select and order items in each column)">
        {taxonomyLoading ? (
          <p className="text-sm text-muted-foreground/70 py-4">Loading taxonomy data...</p>
        ) : (
          <div className="space-y-4">
            <OrderedMenuItemSelector
              label="By Type"
              items={taxonomyData.types}
              nameField="name"
              settingKey="header_menu_selected_types"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
            <OrderedMenuItemSelector
              label="By Occasion (Events)"
              items={taxonomyData.events}
              nameField="name"
              settingKey="header_menu_selected_occasions"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
            <OrderedMenuItemSelector
              label="By Mood"
              items={taxonomyData.moods}
              nameField="name"
              settingKey="header_menu_selected_moods"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
            <OrderedMenuItemSelector
              label="By Place"
              items={taxonomyData.places}
              nameField="name"
              settingKey="header_menu_selected_places"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
            <OrderedMenuItemSelector
              label="By Collection"
              items={taxonomyData.collections}
              nameField="name"
              settingKey="header_menu_selected_collections"
              getSettingValue={getSettingValue}
              onChange={handleChange}
              toast={toast}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground/70 mt-4">
          Use ↑ ↓ to reorder. Changes take effect after clicking "Save Changes". Items only appear if they have at least
          1 published product linked.
        </p>
      </Section>
    </div>
  );
};

/* ====================================================================
   TAB 2: HOME PAGE
   ==================================================================== */
const HomePageTab = ({ settings, getSettingValue, handleChange, dirtyKeys }) => {
  const ourStorySettings = settings.filter(
    (s) => s.key.startsWith('our_story_') && !s.key.startsWith('our_story_page_'),
  );

  return (
    <div className="space-y-8">
      {/* Sections — order + visibility together, since they're one feature */}
      <Section title="Sections">
        <p className="text-sm text-muted-foreground mb-4">
          Reorder and show/hide homepage sections. The Hero Section always stays first so the header never breaks.
        </p>
        <SectionManager sections={HOME_SECTIONS} orderSettingKey="home_sections_order" pinnedFirstKey="hero" />
      </Section>

      {/* Hero Section */}
      <Section title="Hero Section">
        <p className="text-xs text-muted-foreground/70 mb-4">
          Hero slides support both images and videos. Upload mp4/mov files for video slides.
        </p>
        <HeroSlidesManager />
      </Section>

      {/* Shop By Tiles */}
      <Section title="Shop By Tiles">
        <p className="text-xs text-muted-foreground/70 mb-4">
          Configure up to 3 portrait tiles shown below the hero section. Each tile has an image, label, and link.
        </p>
        <ShopTilesManager />
      </Section>

      {/* Product Carousel Titles */}
      <Section title="Product Carousel">
        <p className="text-sm text-muted-foreground mb-4">
          Set the heading text for each featured product row on the homepage.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">New Collection Title</label>
            <input
              type="text"
              value={getSettingValue('new_collection_title')}
              onChange={(e) => handleChange('new_collection_title', 'value', e.target.value)}
              placeholder="e.g. New Collection"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Heading above the horizontal-scroll collections row</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Row 1 Title</label>
            <input
              type="text"
              value={getSettingValue('featured_products_1_title')}
              onChange={(e) => handleChange('featured_products_1_title', 'value', e.target.value)}
              placeholder="e.g. New Arrivals"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Leave empty for no heading</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Row 2 Title</label>
            <input
              type="text"
              value={getSettingValue('featured_products_2_title')}
              onChange={(e) => handleChange('featured_products_2_title', 'value', e.target.value)}
              placeholder="e.g. Apres l'Ete"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
        </div>
      </Section>

      {/* Showcase (Spotted + UGC) */}
      <Section title="Showcase (Seen in KAAY)">
        <p className="text-sm text-muted-foreground mb-4">
          Heading and thresholds for the Spotted + customer photo showcase. Show/hide and reorder it in Sections above.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input
              type="text"
              value={getSettingValue('showcase_heading')}
              onChange={(e) => handleChange('showcase_heading', 'value', e.target.value)}
              placeholder="Seen in KAAY"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Heading above the homepage showcase</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subheading</label>
            <input
              type="text"
              value={getSettingValue('showcase_subheading')}
              onChange={(e) => handleChange('showcase_subheading', 'value', e.target.value)}
              placeholder=""
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Leave empty for no subheading</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Homepage minimum items</label>
            <input
              type="number"
              min="1"
              value={getSettingValue('showcase_home_min_count')}
              onChange={(e) => handleChange('showcase_home_min_count', 'value', e.target.value)}
              placeholder="3"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Homepage section stays hidden until at least this many items are published
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Homepage maximum items</label>
            <input
              type="number"
              min="1"
              value={getSettingValue('showcase_home_max_items')}
              onChange={(e) => handleChange('showcase_home_max_items', 'value', e.target.value)}
              placeholder="8"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Most items shown on the homepage</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Product page minimum items</label>
            <input
              type="number"
              min="1"
              value={getSettingValue('showcase_pdp_min_count')}
              onChange={(e) => handleChange('showcase_pdp_min_count', 'value', e.target.value)}
              placeholder="1"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Product page showcase stays hidden until a product has at least this many items
            </p>
          </div>
        </div>
      </Section>

      {/* Featured Product Order (Outfit Inspiration Slider) */}
      <Section title="Outfit Inspiration Slider — Product Order">
        <p className="text-sm text-muted-foreground mb-4">
          Control the order of featured products. The first 6 appear in the Outfit Inspiration slider on the homepage.
        </p>
        <FeaturedProductsManager />
      </Section>

      {/* Our Story CTA (homepage banner) */}
      <Section title="Our Story CTA Section">
        <p className="text-sm text-muted-foreground mb-4">
          Configure the Our Story banner on the homepage. Supports image or video background.
        </p>
        <OurStoryEditor
          settings={ourStorySettings}
          getSettingValue={getSettingValue}
          onChange={handleChange}
          dirtyKeys={dirtyKeys}
        />
      </Section>

      {/* Trust Pillars — also shown on the Product Page and in the site-wide Footer */}
      <Section title="Trust Pillars (Homepage, Product Page & Footer)">
        <p className="text-sm text-muted-foreground mb-4">
          The "why our pieces cost more" content — shown in the Why KAAY homepage section, the footer trust bar, and on
          every product page. Add, edit, delete, and reorder any pillar; attach extra pillars to a specific product from
          that product's "Extra Trust Pillars" tab.
        </p>
        <TrustPillarsManager />
      </Section>

      {/* Crafted Consciously — optional image row below the trust marks */}
      <Section title="Crafted Consciously — Images">
        <p className="text-sm text-muted-foreground mb-4">
          Optional image row shown below the trust marks in the Crafted Consciously section. Pick a layout pattern,
          choose a height, and upload the images it needs — the row stays hidden on the homepage until every slot is
          filled.
        </p>
        <CraftedConsciouslyImagesManager />
      </Section>

      {/* Instagram Feed Grid Pattern */}
      <Section title="Instagram Feed Grid Pattern">
        <p className="text-sm text-muted-foreground mb-4">
          Choose the layout pattern for the Instagram feed on the homepage.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {[
            {
              value: 'alternating',
              label: 'Alternating',
              desc: 'Wide + small cells alternating',
              preview: (
                <div className="flex gap-0.5 h-full">
                  <div className="flex flex-col gap-0.5 flex-1">
                    <div className="bg-foreground/80 rounded-sm" style={{ height: '65%' }} />
                    <div className="flex gap-0.5" style={{ height: '35%' }}>
                      <div className="bg-muted-foreground/50 rounded-sm flex-1" />
                      <div className="bg-muted-foreground/50 rounded-sm flex-1" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1">
                    <div className="flex gap-0.5" style={{ height: '35%' }}>
                      <div className="bg-muted-foreground/50 rounded-sm flex-1" />
                      <div className="bg-muted-foreground/50 rounded-sm flex-1" />
                    </div>
                    <div className="bg-foreground/80 rounded-sm" style={{ height: '65%' }} />
                  </div>
                </div>
              ),
            },
            {
              value: 'uniform',
              label: 'Uniform',
              desc: 'Equal cells, 2 rows',
              preview: (
                <div className="grid grid-cols-3 grid-rows-2 gap-0.5 h-full">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-muted-foreground rounded-sm" />
                  ))}
                </div>
              ),
            },
            {
              value: 'highlight_first',
              label: 'Highlight',
              desc: 'First post large, rest grid',
              preview: (
                <div className="flex gap-0.5 h-full">
                  <div className="bg-foreground/80 rounded-sm" style={{ width: '50%' }} />
                  <div className="grid grid-cols-2 grid-rows-2 gap-0.5 flex-1">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="bg-muted-foreground/50 rounded-sm" />
                    ))}
                  </div>
                </div>
              ),
            },
            {
              value: 'single_row',
              label: 'Single Row',
              desc: 'One tall row',
              preview: (
                <div className="flex gap-0.5 h-full">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-muted-foreground rounded-sm flex-1" />
                  ))}
                </div>
              ),
            },
            {
              value: 'three_row',
              label: 'Three Row',
              desc: 'Dense 3-row mosaic',
              preview: (
                <div className="grid grid-cols-4 grid-rows-3 gap-0.5 h-full">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="bg-muted-foreground rounded-sm" />
                  ))}
                </div>
              ),
            },
          ].map((pattern) => {
            const selected = getSettingValue('instagram_grid_pattern') === pattern.value;
            return (
              <button
                key={pattern.value}
                onClick={() => handleChange('instagram_grid_pattern', 'value', pattern.value)}
                className={`flex flex-col border-2 rounded-lg p-2 transition-all text-left ${
                  selected
                    ? 'border-foreground bg-muted/50 ring-1 ring-foreground'
                    : 'border-border hover:border-muted-foreground'
                }`}
              >
                <div className="w-full aspect-[4/3] mb-2">{pattern.preview}</div>
                <span className={`text-xs font-medium ${selected ? 'text-foreground' : 'text-foreground/80'}`}>
                  {pattern.label}
                </span>
                <span className="text-[10px] text-muted-foreground/70 leading-tight">{pattern.desc}</span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Collection Welcome Text */}
      <Section title="Collection Welcome Text">
        <p className="text-sm text-muted-foreground mb-4">
          Configure the welcome text section on the homepage. Use \n for line breaks in the heading.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input
              type="text"
              value={getSettingValue('collection_welcome_heading')}
              onChange={(e) => handleChange('collection_welcome_heading', 'value', e.target.value)}
              placeholder="e.g. Welcome to\nSummer 26"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Use \n for line breaks</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              rows={4}
              value={getSettingValue('collection_welcome_description')}
              onChange={(e) => handleChange('collection_welcome_description', 'value', e.target.value)}
              placeholder="Welcome paragraph text..."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Separate paragraphs with double newlines</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">CTA Button Text</label>
              <input
                type="text"
                value={getSettingValue('collection_welcome_cta_text')}
                onChange={(e) => handleChange('collection_welcome_cta_text', 'value', e.target.value)}
                placeholder="EXPLORE THE COLLECTION"
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CTA Link</label>
              <input
                type="text"
                value={getSettingValue('collection_welcome_cta_link')}
                onChange={(e) => handleChange('collection_welcome_cta_link', 'value', e.target.value)}
                placeholder="/ready-to-wear"
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

/* ====================================================================
   TAB: PRODUCT PAGE (PDP)
   ==================================================================== */
const ProductPageTab = ({ settings, handleToggleSection }) => {
  const sectionToggles = settings.filter((s) => s.key.startsWith('pdp_section_') && s.key.endsWith('_enabled'));

  const sectionLabels = {
    pdp_section_related_products_enabled: 'Related Products (More Like This)',
    pdp_section_reviews_enabled: 'Customer Reviews & Testimonials',
    pdp_section_write_review_enabled: 'Write a Review (verified purchasers)',
    pdp_section_instagram_enabled: 'Instagram Feed',
    pdp_section_spotted_enabled: 'Spotted (press & celebrity showcase)',
    pdp_section_ugc_enabled: 'Worn by you (customer photos)',
    pdp_section_review_badges_enabled: 'Review Source Badges ("Verified Purchase" / "Provided by Kaay")',
  };

  return (
    <div className="space-y-8">
      <Section title="Section Visibility">
        <p className="text-sm text-muted-foreground mb-4">
          Control which sections are visible on the product detail page.
        </p>
        <div className="space-y-3">
          {sectionToggles.map((setting) => {
            const isEnabled = setting.value === 'true';
            return (
              <div
                key={setting.key}
                className={`flex items-center justify-between p-3 rounded-lg border transition ${
                  isEnabled ? 'border-border bg-card' : 'border-border/60 bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isEnabled ? (
                    <Eye size={18} className="text-green-600 dark:text-green-400" />
                  ) : (
                    <EyeOff size={18} className="text-muted-foreground/70" />
                  )}
                  <span className={`text-sm ${isEnabled ? 'text-foreground' : 'text-muted-foreground/70'}`}>
                    {sectionLabels[setting.key] || setting.key}
                  </span>
                </div>
                <button
                  onClick={() => handleToggleSection(setting)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            );
          })}
          {sectionToggles.length === 0 && (
            <p className="text-center text-muted-foreground/70 py-4">
              No PDP section toggles found. Run the seed script to create them.
            </p>
          )}
        </div>
      </Section>
    </div>
  );
};

/* ====================================================================
   TAB 3: ABOUT PAGE
   ==================================================================== */
const AboutPageTab = ({ getSettingValue, handleChange, saveOrCreate, settings, toast }) => {
  const [showSection1Upload, setShowSection1Upload] = useState(false);
  const [showSection1MobileUpload, setShowSection1MobileUpload] = useState(false);
  const [showSection3Upload, setShowSection3Upload] = useState(false);
  const [showSection4Upload, setShowSection4Upload] = useState(false);

  return (
    <div className="space-y-8">
      {/* Section 1: Hero */}
      <Section title="Section 1: Hero">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ImageUploadField
            label="Desktop Hero Image (Landscape)"
            imageUrl={getSettingValue('about_section_1_image')}
            settingKey="about_section_1_image"
            showUpload={showSection1Upload}
            setShowUpload={setShowSection1Upload}
            onChange={handleChange}
          />
          <ImageUploadField
            label="Mobile Hero Image (Portrait)"
            imageUrl={getSettingValue('about_section_1_mobile_image')}
            settingKey="about_section_1_mobile_image"
            showUpload={showSection1MobileUpload}
            setShowUpload={setShowSection1MobileUpload}
            onChange={handleChange}
          />
        </div>
      </Section>

      {/* Section 2: Introduction */}
      <Section title="Section 2: Introduction">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={getSettingValue('about_section_2_title')}
              onChange={(e) => handleChange('about_section_2_title', 'value', e.target.value)}
              placeholder="Our Story"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content (Markdown)</label>
            <textarea
              value={getSettingValue('about_section_2_content')}
              onChange={(e) => handleChange('about_section_2_content', 'value', e.target.value)}
              rows={8}
              placeholder="Write your brand introduction using markdown..."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Supports markdown: **bold**, *italic*, ## headings, - bullet lists, 1. numbered lists, [links](url)
            </p>
          </div>
        </div>
      </Section>

      {/* Section 3: Origin Story */}
      <Section title="Section 3: Origin Story">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={getSettingValue('about_section_3_title')}
              onChange={(e) => handleChange('about_section_3_title', 'value', e.target.value)}
              placeholder="How It All Started"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              type="text"
              value={getSettingValue('about_section_3_subtitle')}
              onChange={(e) => handleChange('about_section_3_subtitle', 'value', e.target.value)}
              placeholder="A subtitle for this section"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content (Markdown)</label>
            <textarea
              value={getSettingValue('about_section_3_content')}
              onChange={(e) => handleChange('about_section_3_content', 'value', e.target.value)}
              rows={8}
              placeholder="Tell the origin story using markdown..."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Supports markdown: **bold**, *italic*, ## headings, - bullet lists, 1. numbered lists, [links](url)
            </p>
          </div>
          <ImageUploadField
            label="Section Image"
            imageUrl={getSettingValue('about_section_3_image')}
            settingKey="about_section_3_image"
            showUpload={showSection3Upload}
            setShowUpload={setShowSection3Upload}
            onChange={handleChange}
          />
        </div>
      </Section>

      {/* Section 4: Inspiration */}
      <Section title="Section 4: Inspiration">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={getSettingValue('about_section_4_title')}
              onChange={(e) => handleChange('about_section_4_title', 'value', e.target.value)}
              placeholder="Our Inspiration"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              type="text"
              value={getSettingValue('about_section_4_subtitle')}
              onChange={(e) => handleChange('about_section_4_subtitle', 'value', e.target.value)}
              placeholder="A subtitle for this section"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content (Markdown)</label>
            <textarea
              value={getSettingValue('about_section_4_content')}
              onChange={(e) => handleChange('about_section_4_content', 'value', e.target.value)}
              rows={8}
              placeholder="Describe your inspiration using markdown..."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Supports markdown: **bold**, *italic*, ## headings, - bullet lists, 1. numbered lists, [links](url)
            </p>
          </div>
          <ImageUploadField
            label="Section Image"
            imageUrl={getSettingValue('about_section_4_image')}
            settingKey="about_section_4_image"
            showUpload={showSection4Upload}
            setShowUpload={setShowSection4Upload}
            onChange={handleChange}
          />
        </div>
      </Section>

      {/* Section 5: CTA */}
      <Section title="Section 5: CTA">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={getSettingValue('about_section_5_title')}
              onChange={(e) => handleChange('about_section_5_title', 'value', e.target.value)}
              placeholder="Experience KAAY"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content (Markdown)</label>
            <textarea
              value={getSettingValue('about_section_5_content')}
              onChange={(e) => handleChange('about_section_5_content', 'value', e.target.value)}
              rows={5}
              placeholder="Discover our latest designs..."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">
              Supports markdown: **bold**, *italic*, ## headings, - bullet lists, 1. numbered lists, [links](url)
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};

/* ====================================================================
   TAB 4: NEWSLETTER
   ==================================================================== */
const NewsletterTab = ({ getSettingValue, handleChange }) => {
  const [showImageUpload, setShowImageUpload] = useState(false);
  const newsletterImage = getSettingValue('newsletter_image');
  const isEnabled = getSettingValue('newsletter_enabled') === 'true';

  return (
    <div className="space-y-8">
      <Section title="Newsletter Popup">
        <p className="text-sm text-muted-foreground mb-4">
          Configure the newsletter subscription popup for new visitors.
        </p>
        <div className="space-y-4">
          {/* Enabled Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <span className="text-sm font-medium">Enable Newsletter Popup</span>
            <button
              type="button"
              onClick={() => handleChange('newsletter_enabled', 'value', isEnabled ? 'false' : 'true')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium mb-2">Popup Image</label>
            {newsletterImage ? (
              <div className="flex flex-wrap items-start gap-4">
                <div className="relative w-[200px] h-[120px] bg-muted rounded-lg overflow-hidden group">
                  <img src={newsletterImage} alt="Newsletter popup" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setShowImageUpload(true)}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <RefreshCw size={20} className="text-white" />
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setShowImageUpload(true)}
                    className="text-xs text-muted-foreground hover:text-foreground underline text-left"
                  >
                    Replace
                  </button>
                  <button
                    onClick={() => handleChange('newsletter_image', 'value', '')}
                    className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 underline text-left"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowImageUpload(true)}
                className="w-full p-6 border-2 border-dashed border-input rounded-lg text-muted-foreground/70 hover:border-foreground hover:text-foreground transition text-center text-sm"
              >
                <Upload size={24} className="mx-auto mb-2" />
                Upload Popup Image
              </button>
            )}
            {showImageUpload && (
              <div className="mt-3 border border-border rounded-lg p-4">
                <FileUpload
                  uploadType="about-media"
                  accept="image/*"
                  multiple={false}
                  maxFiles={1}
                  onUploadComplete={(files) => {
                    const file = files[0];
                    if (file?.uploaded && file?.url) {
                      handleChange('newsletter_image', 'value', file.url);
                      setShowImageUpload(false);
                    }
                  }}
                />
                <button
                  onClick={() => setShowImageUpload(false)}
                  className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <X size={12} /> Cancel
                </button>
              </div>
            )}
          </div>

          {/* Heading */}
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input
              type="text"
              value={getSettingValue('newsletter_heading')}
              onChange={(e) => handleChange('newsletter_heading', 'value', e.target.value)}
              placeholder="Join the KAAY Community"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>

          {/* Subtext */}
          <div>
            <label className="block text-sm font-medium mb-1">Subtext</label>
            <textarea
              value={getSettingValue('newsletter_subtext')}
              onChange={(e) => handleChange('newsletter_subtext', 'value', e.target.value)}
              rows={3}
              placeholder="Subscribe to get exclusive access to new collections, special offers, and style inspiration."
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>

          {/* CTA Button Text */}
          <div>
            <label className="block text-sm font-medium mb-1">CTA Button Text</label>
            <input
              type="text"
              value={getSettingValue('newsletter_cta_text')}
              onChange={(e) => handleChange('newsletter_cta_text', 'value', e.target.value)}
              placeholder="Subscribe"
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
            />
          </div>
        </div>
      </Section>
    </div>
  );
};

/* ====================================================================
   SHARED COMPONENTS
   ==================================================================== */

/* Simple section wrapper (replaces accordion) */
const Section = ({ title, children, icon = null }) => (
  <div className="bg-card border border-border rounded-lg p-6">
    <h2 className="text-lg font-serif mb-4">{title}</h2>
    {children}
  </div>
);

/* Toggle row used in Header tab */
const ToggleRow = ({ label, isEnabled, onToggle }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
    <span className="text-sm">{label}</span>
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
          isEnabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

/* Image Upload Field — reusable */
const ImageUploadField = ({ label, imageUrl, settingKey, showUpload, setShowUpload, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-2">{label}</label>
    {imageUrl ? (
      <div className="flex flex-wrap items-start gap-4">
        <div className="relative w-[200px] h-[120px] bg-muted rounded-lg overflow-hidden group">
          <img src={imageUrl} alt={label} className="w-full h-full object-cover" />
          <button
            onClick={() => setShowUpload(true)}
            className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <RefreshCw size={20} className="text-white" />
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => setShowUpload(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline text-left"
          >
            Replace
          </button>
          <button
            onClick={() => onChange(settingKey, 'value', '')}
            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 underline text-left"
          >
            Remove
          </button>
        </div>
      </div>
    ) : (
      <button
        onClick={() => setShowUpload(true)}
        className="w-full p-6 border-2 border-dashed border-input rounded-lg text-muted-foreground/70 hover:border-foreground hover:text-foreground transition text-center text-sm"
      >
        <Upload size={24} className="mx-auto mb-2" />
        Upload {label}
      </button>
    )}
    {showUpload && (
      <div className="mt-3 border border-border rounded-lg p-4">
        <FileUpload
          uploadType="about-media"
          accept="image/*"
          multiple={false}
          maxFiles={1}
          onUploadComplete={(files) => {
            const file = files[0];
            if (file?.uploaded && file?.url) {
              onChange(settingKey, 'value', file.url);
              setShowUpload(false);
            }
          }}
        />
        <button
          onClick={() => setShowUpload(false)}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    )}
  </div>
);

/* Our Story Editor (Homepage CTA banner) */
const OurStoryEditor = ({ settings, getSettingValue, onChange, dirtyKeys }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const mediaUrl = getSettingValue('our_story_media_url');
  const mediaType = getSettingValue('our_story_media_type');
  const mediaIsUnsaved = dirtyKeys?.has('our_story_media_url') || dirtyKeys?.has('our_story_media_type');

  const handleMediaUpload = (files) => {
    const file = files[0];
    if (!file?.uploaded || !file?.url) return;
    onChange('our_story_media_url', 'value', file.url);
    onChange('our_story_media_type', 'value', file.resource_type === 'video' ? 'video' : 'image');
    setShowUpload(false);
  };

  const handleMediaPicked = (url) => {
    onChange('our_story_media_url', 'value', url);
    onChange('our_story_media_type', 'value', 'video');
    setShowPicker(false);
  };

  const getThumbnailUrl = () => {
    if (!mediaUrl) return null;
    if (mediaType === 'video') {
      return getVideoThumbnailUrl(mediaUrl, { width: 400 });
    }
    return mediaUrl;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Heading Text</label>
          <input
            type="text"
            value={getSettingValue('our_story_heading')}
            onChange={(e) => onChange('our_story_heading', 'value', e.target.value)}
            placeholder="Our Story"
            className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Button Text</label>
          <input
            type="text"
            value={getSettingValue('our_story_button_text')}
            onChange={(e) => onChange('our_story_button_text', 'value', e.target.value)}
            placeholder="Discover More"
            className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Button Link</label>
        <input
          type="text"
          value={getSettingValue('our_story_button_link')}
          onChange={(e) => onChange('our_story_button_link', 'value', e.target.value)}
          placeholder="/about"
          className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
        />
      </div>

      {/* Video aspect ratio per breakpoint (height follows width, so the shape is identical on every device) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RatioPicker
          label="Video Shape — Desktop"
          hint="md screens and up · video only"
          options={DESKTOP_RATIOS}
          value={getSettingValue('our_story_ratio_desktop')}
          onChange={(v) => onChange('our_story_ratio_desktop', 'value', v)}
        />
        <RatioPicker
          label="Video Shape — Mobile"
          hint="below md · video only"
          options={MOBILE_RATIOS}
          value={getSettingValue('our_story_ratio_mobile')}
          onChange={(v) => onChange('our_story_ratio_mobile', 'value', v)}
        />
      </div>

      {mediaUrl && mediaType === 'video' && (
        <OurStoryPreview
          mediaUrl={mediaUrl}
          ratioDesktop={getSettingValue('our_story_ratio_desktop')}
          ratioMobile={getSettingValue('our_story_ratio_mobile')}
          heading={getSettingValue('our_story_heading')}
          buttonText={getSettingValue('our_story_button_text')}
        />
      )}

      {/* Media Upload */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium">Background Media (Image or Video)</label>
          {mediaIsUnsaved && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> Unsaved — click Save below to apply
            </span>
          )}
        </div>
        {mediaUrl ? (
          <div className="flex flex-wrap items-start gap-4">
            <div className="relative w-[200px] h-[120px] bg-gray-900 rounded-lg overflow-hidden group">
              {mediaType === 'video' ? (
                // A real player, not just a poster frame — lets you actually watch it (with
                // sound) before publishing, instead of only seeing a static first frame.
                <video
                  src={mediaUrl}
                  poster={getThumbnailUrl() || undefined}
                  controls
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <img src={mediaUrl} alt="Our Story preview" className="w-full h-full object-cover" />
              )}
              <button
                onClick={() => setShowUpload(true)}
                className="absolute top-1 right-1 p-1.5 bg-black/60 rounded-lg opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 transition-opacity"
                title="Replace media"
              >
                <RefreshCw size={14} className="text-white" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 w-fit ${
                  mediaType === 'video'
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                    : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                }`}
              >
                {mediaType === 'video' ? (
                  <>
                    <Film size={10} /> Video
                  </>
                ) : (
                  <>
                    <Image size={10} /> Image
                  </>
                )}
              </span>
              <button
                onClick={() => setShowUpload(true)}
                className="text-xs text-muted-foreground hover:text-foreground underline text-left"
              >
                Replace
              </button>
              <button
                onClick={() => setShowPicker(true)}
                className="text-xs text-muted-foreground hover:text-foreground underline text-left"
              >
                Choose existing video
              </button>
              <button
                onClick={() => {
                  onChange('our_story_media_url', 'value', '');
                  onChange('our_story_media_type', 'value', 'image');
                }}
                className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 underline text-left"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => setShowUpload(true)}
              className="w-full p-8 border-2 border-dashed border-input rounded-lg text-muted-foreground/70 hover:border-foreground hover:text-foreground transition text-center text-sm"
            >
              <Upload size={24} className="mx-auto mb-2" />
              Upload Image or Video
            </button>
            <button
              onClick={() => setShowPicker(true)}
              className="w-full text-xs text-muted-foreground hover:text-foreground underline text-center"
            >
              Or choose a previously uploaded video
            </button>
          </div>
        )}

        {showUpload && (
          <div className="mt-3 border border-border rounded-lg p-4">
            <FileUpload
              uploadType="about-media"
              accept="image/*,video/*"
              multiple={false}
              maxFiles={1}
              onUploadComplete={handleMediaUpload}
            />
            <button
              onClick={() => setShowUpload(false)}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        )}

        {showPicker && (
          <MediaJobPicker context="about_us" onSelect={handleMediaPicked} onCancel={() => setShowPicker(false)} />
        )}
      </div>
    </div>
  );
};

/* Logo Upload Card */
const LogoUploadCard = ({ label, description, previewBg, logoUrl, onUpload, onRemove }) => {
  const [showUpload, setShowUpload] = useState(false);

  return (
    <div className="border border-border rounded-lg p-4">
      <h4 className="text-sm font-medium mb-1">{label}</h4>
      <p className="text-xs text-muted-foreground/70 mb-3">{description}</p>

      {logoUrl ? (
        <div className="flex flex-col items-center gap-3">
          <div className={`${previewBg} p-4 rounded-lg border border-border w-full flex justify-center`}>
            <img src={logoUrl} alt={label} className="h-14 object-contain" />
          </div>
          {showUpload ? (
            <div className="w-full">
              <FileUpload
                uploadType="product-image"
                accept="image/*"
                multiple={false}
                maxFiles={1}
                onUploadComplete={(files) => {
                  const f = files[0];
                  if (f && f.uploaded && f.url) onUpload(f.url);
                  setShowUpload(false);
                }}
              />
              <button
                onClick={() => setShowUpload(false)}
                className="mt-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setShowUpload(true)}
                className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-accent transition"
              >
                Replace
              </button>
              <button
                onClick={onRemove}
                className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 rounded-md hover:bg-red-50 dark:hover:bg-red-950/70 transition"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ) : showUpload ? (
        <div>
          <FileUpload
            uploadType="product-image"
            accept="image/*"
            multiple={false}
            maxFiles={1}
            onUploadComplete={(files) => {
              const f = files[0];
              if (f && f.uploaded && f.url) onUpload(f.url);
              setShowUpload(false);
            }}
          />
          <button
            onClick={() => setShowUpload(false)}
            className="mt-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowUpload(true)}
          className="w-full p-6 border-2 border-dashed border-input rounded-lg text-muted-foreground/70 hover:border-foreground hover:text-foreground transition text-center text-sm"
        >
          Upload {label}
        </button>
      )}
    </div>
  );
};

/* OrderedMenuItemSelector — shows selected items as an ordered list with up/down controls,
   and unselected items as checkboxes to add. The order of IDs in the setting value
   is preserved and sent to the backend. */
const OrderedMenuItemSelector = ({ label, items, nameField, settingKey, getSettingValue, onChange, toast }) => {
  const selectedIds = (getSettingValue(settingKey) || '').split(',').filter((id) => id.trim() !== '');
  const selectedSet = new Set(selectedIds);

  const selectedItems = selectedIds.map((id) => items.find((i) => String(i.id) === id)).filter(Boolean);
  const availableItems = items.filter((i) => !selectedSet.has(String(i.id)));

  const add = (itemId) => {
    const idStr = String(itemId);
    const item = items.find((i) => String(i.id) === idStr);
    if (item && item.product_count != null && item.product_count === 0 && toast) {
      toast.warning(`"${item[nameField]}" has no products yet.`);
    }
    onChange(settingKey, 'value', [...selectedIds, idStr].join(','));
  };

  const remove = (itemId) => {
    onChange(settingKey, 'value', selectedIds.filter((id) => id !== String(itemId)).join(','));
  };

  const move = (index, direction) => {
    const updated = [...selectedIds];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    onChange(settingKey, 'value', updated.join(','));
  };

  if (items.length === 0) {
    return (
      <div className="border border-border/60 rounded-lg p-3">
        <h5 className="text-xs font-medium text-muted-foreground mb-1">{label}</h5>
        <p className="text-xs text-muted-foreground/70">No items found in this category.</p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 rounded-lg p-3 space-y-3">
      <h5 className="text-xs font-medium text-muted-foreground">{label}</h5>

      {/* Selected items — ordered */}
      {selectedItems.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground/70 mb-1">Showing in this order ↓</p>
          {selectedItems.map((item, index) => (
            <div key={item.id} className="flex items-center justify-between bg-black/5 rounded-md px-2 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground/70 w-4 text-center font-mono">{index + 1}</span>
                <span className="text-foreground">{item[nameField]}</span>
                {item.product_count != null && (
                  <span
                    className={`text-xs ${item.product_count === 0 ? 'text-amber-500' : 'text-muted-foreground/70'}`}
                  >
                    ({item.product_count})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="p-0.5 text-muted-foreground/70 hover:text-foreground disabled:opacity-20"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === selectedItems.length - 1}
                  className="p-0.5 text-muted-foreground/70 hover:text-foreground disabled:opacity-20"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  className="p-0.5 text-red-400 hover:text-red-600 dark:hover:text-red-400 ml-1"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available items — checkboxes to add */}
      {availableItems.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground/70 mb-1">Add to list</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {availableItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => add(item.id)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-muted/50 border border-dashed border-border text-left"
              >
                <span className="text-muted-foreground/70">+</span>
                <span className={`truncate ${item.product_count === 0 ? 'text-muted-foreground/70' : ''}`}>
                  {item[nameField]}
                </span>
                {item.product_count != null && (
                  <span
                    className={`text-xs ${item.product_count === 0 ? 'text-amber-500' : 'text-muted-foreground/70'}`}
                  >
                    ({item.product_count})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedItems.length === 0 && availableItems.length === 0 && (
        <p className="text-xs text-muted-foreground/70">No items available.</p>
      )}
    </div>
  );
};

/* ====================================================================
   TAB: SHIPPING
   ==================================================================== */
const ShippingTab = ({ toast }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCountry, setNewCountry] = useState({});

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const allSettings = await api.get('/api/site-settings/');
        const shippingSetting = allSettings.find((s) => s.key === 'shipping_config');
        if (shippingSetting && shippingSetting.value) {
          setConfig(JSON.parse(shippingSetting.value));
        } else {
          // Default config
          setConfig({
            free_threshold_inr: 40000,
            zones: [
              { name: 'India', countries: ['IN'], rate_inr: 500, min_days: 3, max_days: 5 },
              {
                name: 'Asia',
                countries: [
                  'AE',
                  'SG',
                  'MY',
                  'TH',
                  'PH',
                  'ID',
                  'JP',
                  'KR',
                  'CN',
                  'HK',
                  'TW',
                  'BD',
                  'LK',
                  'NP',
                  'PK',
                  'QA',
                  'KW',
                  'OM',
                  'BH',
                  'SA',
                ],
                rate_inr: 1500,
                min_days: 7,
                max_days: 10,
              },
              {
                name: 'US / UK / EU',
                countries: [
                  'US',
                  'GB',
                  'UK',
                  'DE',
                  'FR',
                  'IT',
                  'ES',
                  'NL',
                  'BE',
                  'AT',
                  'CH',
                  'SE',
                  'NO',
                  'DK',
                  'FI',
                  'IE',
                  'PT',
                  'PL',
                  'CZ',
                  'GR',
                  'HU',
                  'RO',
                  'CA',
                  'AU',
                  'NZ',
                ],
                rate_inr: 2500,
                min_days: 7,
                max_days: 12,
              },
            ],
            default_rate_inr: 3000,
            default_min_days: 10,
            default_max_days: 15,
            return_window_days: 7,
            refund_processing_days_min: 5,
            refund_processing_days_max: 7,
          });
        }
      } catch (err) {
        toast.error('Failed to load shipping config');
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allSettings = await api.get('/api/site-settings/');
      const existing = allSettings.find((s) => s.key === 'shipping_config');
      if (existing) {
        await api.put('/api/site-settings/shipping_config', { value: JSON.stringify(config) });
      } else {
        await api.post('/api/site-settings/', {
          key: 'shipping_config',
          value: JSON.stringify(config),
          is_active: true,
        });
      }
      toast.success('Shipping config saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save shipping config');
    } finally {
      setSaving(false);
    }
  };

  const updateZone = (zoneIdx, field, value) => {
    setConfig((prev) => {
      const zones = [...prev.zones];
      zones[zoneIdx] = { ...zones[zoneIdx], [field]: value };
      return { ...prev, zones };
    });
  };

  const removeCountryFromZone = (zoneIdx, countryCode) => {
    setConfig((prev) => {
      const zones = [...prev.zones];
      zones[zoneIdx] = { ...zones[zoneIdx], countries: zones[zoneIdx].countries.filter((c) => c !== countryCode) };
      return { ...prev, zones };
    });
  };

  const addCountryToZone = (zoneIdx) => {
    const code = (newCountry[zoneIdx] || '').trim().toUpperCase();
    if (!code) return;
    setConfig((prev) => {
      const zones = [...prev.zones];
      if (!zones[zoneIdx].countries.includes(code)) {
        zones[zoneIdx] = { ...zones[zoneIdx], countries: [...zones[zoneIdx].countries, code] };
      }
      return { ...prev, zones };
    });
    setNewCountry((prev) => ({ ...prev, [zoneIdx]: '' }));
  };

  const addZone = () => {
    setConfig((prev) => ({
      ...prev,
      zones: [...prev.zones, { name: '', countries: [], rate_inr: 0, min_days: 7, max_days: 14 }],
    }));
  };

  const removeZone = (zoneIdx) => {
    setConfig((prev) => ({
      ...prev,
      zones: prev.zones.filter((_, i) => i !== zoneIdx),
    }));
  };

  if (loading || !config)
    return <div className="text-center py-12 text-muted-foreground">Loading shipping config...</div>;

  return (
    <div className="space-y-6">
      {/* Free Shipping Threshold */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Free Shipping Threshold</h3>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Threshold (INR)</label>
          <input
            type="number"
            value={config.free_threshold_inr}
            onChange={(e) => setConfig({ ...config, free_threshold_inr: parseInt(e.target.value) || 0 })}
            className="w-40 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
          />
          <span className="text-sm text-muted-foreground">Orders above this amount get free shipping</span>
        </div>
      </div>

      {/* Shipping Zones */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium">Shipping Zones</h3>
          <button
            onClick={addZone}
            className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90"
          >
            + Add Zone
          </button>
        </div>

        <div className="space-y-4">
          {config.zones.map((zone, idx) => (
            <div key={idx} className="border border-border rounded-lg p-4">
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Zone Name</label>
                  <input
                    type="text"
                    value={zone.name}
                    onChange={(e) => updateZone(idx, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                    placeholder="e.g., Asia"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Rate (INR)</label>
                  <input
                    type="number"
                    value={zone.rate_inr}
                    onChange={(e) => updateZone(idx, 'rate_inr', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Min Days</label>
                  <input
                    type="number"
                    min="1"
                    value={zone.min_days ?? ''}
                    onChange={(e) => updateZone(idx, 'min_days', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Max Days</label>
                  <input
                    type="number"
                    min="1"
                    value={zone.max_days ?? ''}
                    onChange={(e) => updateZone(idx, 'max_days', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <button
                  onClick={() => removeZone(idx)}
                  className="mt-5 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/70 rounded-lg"
                  title="Remove zone"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Countries */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Countries</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {zone.countries.map((cc) => (
                    <span key={cc} className="inline-flex items-center gap-1 bg-muted text-sm px-2 py-0.5 rounded-md">
                      {cc}
                      <button
                        onClick={() => removeCountryFromZone(idx, cc)}
                        className="text-muted-foreground/70 hover:text-red-500"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCountry[idx] || ''}
                    onChange={(e) => setNewCountry({ ...newCountry, [idx]: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCountryToZone(idx))}
                    placeholder="Country code (e.g., US)"
                    className="w-40 px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <button
                    onClick={() => addCountryToZone(idx)}
                    className="px-3 py-1.5 bg-muted text-sm rounded-lg hover:bg-accent"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Default Rate */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Default Rate (Rest of World)</h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Rate (INR)</label>
            <input
              type="number"
              value={config.default_rate_inr}
              onChange={(e) => setConfig({ ...config, default_rate_inr: parseInt(e.target.value) || 0 })}
              className="w-32 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Min Days</label>
            <input
              type="number"
              min="1"
              value={config.default_min_days ?? 10}
              onChange={(e) => setConfig({ ...config, default_min_days: parseInt(e.target.value) || 1 })}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Max Days</label>
            <input
              type="number"
              min="1"
              value={config.default_max_days ?? 15}
              onChange={(e) => setConfig({ ...config, default_max_days: parseInt(e.target.value) || 1 })}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <span className="text-sm text-muted-foreground">Applied to countries not in any zone</span>
        </div>
      </div>

      {/* Made to order */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-1">Made to Order</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Making time shown on every product, in the cart, at checkout and on the order confirmation, before the
          delivery estimate.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground/80">
            <input
              type="checkbox"
              checked={(config.made_to_order ?? {}).enabled !== false}
              onChange={(e) =>
                setConfig({
                  ...config,
                  made_to_order: {
                    ...(config.made_to_order ?? { enabled: true, min_days: 3, max_days: 5 }),
                    enabled: e.target.checked,
                  },
                })
              }
            />
            Pieces are made to order
          </label>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">
              Dispatched in (days) from
            </label>
            <input
              type="number"
              min="1"
              value={(config.made_to_order ?? {}).min_days ?? 3}
              onChange={(e) =>
                setConfig({
                  ...config,
                  made_to_order: {
                    ...(config.made_to_order ?? { enabled: true, min_days: 3, max_days: 5 }),
                    min_days: parseInt(e.target.value) || 1,
                  },
                })
              }
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
            <label className="text-sm font-medium text-foreground/80">to</label>
            <input
              type="number"
              min="1"
              value={(config.made_to_order ?? {}).max_days ?? 5}
              onChange={(e) =>
                setConfig({
                  ...config,
                  made_to_order: {
                    ...(config.made_to_order ?? { enabled: true, min_days: 3, max_days: 5 }),
                    max_days: parseInt(e.target.value) || 1,
                  },
                })
              }
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
        </div>
      </div>

      {/* Return & Refund Policy */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Return &amp; Refund Policy</h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Return window (days)</label>
            <input
              type="number"
              min="1"
              value={config.return_window_days ?? 7}
              onChange={(e) => setConfig({ ...config, return_window_days: parseInt(e.target.value) || 1 })}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">
              Refund processing — min days
            </label>
            <input
              type="number"
              min="1"
              value={config.refund_processing_days_min ?? 5}
              onChange={(e) => setConfig({ ...config, refund_processing_days_min: parseInt(e.target.value) || 1 })}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">
              Refund processing — max days
            </label>
            <input
              type="number"
              min="1"
              value={config.refund_processing_days_max ?? 7}
              onChange={(e) => setConfig({ ...config, refund_processing_days_max: parseInt(e.target.value) || 1 })}
              className="w-20 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <span className="text-sm text-muted-foreground">
            Drives the return window and refund timing shown on the product page, cart, and order confirmation — keep
            this in sync with the wording on the Returns policy page.
          </span>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Shipping Config'}
        </button>
      </div>
    </div>
  );
};

/* ====================================================================
   TAB: PAYMENT
   ==================================================================== */
const PaymentTab = ({ toast }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const allSettings = await api.get('/api/site-settings/');
        const setting = allSettings.find((s) => s.key === 'payment_config');
        if (setting && setting.value) {
          setConfig(JSON.parse(setting.value));
        } else {
          // Defaults — must match get_payment_config()'s fallback in backend/routes/orders.py
          setConfig({
            reservation_minutes: 30,
            reminder_1_minutes: 10,
            reminder_2_minutes: 20,
          });
        }
      } catch (err) {
        toast.error('Failed to load payment config');
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allSettings = await api.get('/api/site-settings/');
      const existing = allSettings.find((s) => s.key === 'payment_config');
      if (existing) {
        await api.put('/api/site-settings/payment_config', { value: JSON.stringify(config) });
      } else {
        await api.post('/api/site-settings/', {
          key: 'payment_config',
          value: JSON.stringify(config),
          is_active: true,
        });
      }
      toast.success('Payment config saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save payment config');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config)
    return <div className="text-center py-12 text-muted-foreground">Loading payment config...</div>;

  const reminderFields = [
    { key: 'reminder_1_minutes', label: 'First reminder (minutes after order placed)' },
    { key: 'reminder_2_minutes', label: 'Second reminder (minutes after order placed)' },
  ];
  const reminderPastWindow = reminderFields.some(({ key }) => (config[key] ?? 0) >= (config.reservation_minutes ?? 0));

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Payment Reservation Window</h3>
        <p className="text-sm text-muted-foreground mb-4">
          When a customer places an order, their items are held while they complete payment. If payment isn&apos;t
          completed within this window, the order is cancelled automatically and stock is released.
        </p>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">
            Reservation window (minutes)
          </label>
          <input
            type="number"
            min="1"
            value={config.reservation_minutes ?? 30}
            onChange={(e) => setConfig({ ...config, reservation_minutes: parseInt(e.target.value) || 1 })}
            className="w-24 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Payment Reminder Emails</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Sent automatically to a customer while their order is still awaiting payment, using the "Payment Reminder"
          template (Email Templates page).
        </p>
        <div className="space-y-4">
          {reminderFields.map(({ key, label }) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <label className="text-sm font-medium text-foreground/80 sm:whitespace-nowrap sm:w-80">{label}</label>
              <input
                type="number"
                min="1"
                value={config[key] ?? ''}
                onChange={(e) => setConfig({ ...config, [key]: parseInt(e.target.value) || 1 })}
                className="w-24 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
              />
            </div>
          ))}
        </div>
        {reminderPastWindow && (
          <div className="mt-4 flex items-start gap-2 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              One of the reminders above is set at or after the reservation window, so it won&apos;t be sent — the order
              will have already expired by then.
            </span>
          </div>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4 text-xs text-blue-800 dark:text-blue-300">
        Changes here only apply to orders placed after you save — an order already awaiting payment keeps whichever
        window and reminder timing were active when it was created.
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Payment Config'}
        </button>
      </div>
    </div>
  );
};

/* ====================================================================
   TAB: SIZE RECOMMENDER
   ==================================================================== */
const BODY_SHAPES = ['hourglass', 'pear', 'apple', 'rectangle', 'inverted_triangle'];
const BODY_SHAPE_LABELS = {
  hourglass: 'Hourglass',
  pear: 'Pear',
  apple: 'Apple',
  rectangle: 'Rectangle',
  inverted_triangle: 'Inverted Triangle',
};

const SizeRecommenderTab = ({ toast }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const allSettings = await api.get('/api/site-settings/');
        const setting = allSettings.find((s) => s.key === 'size_recommendation_rules');
        if (setting && setting.value) {
          setConfig(JSON.parse(setting.value));
        } else {
          setConfig({ rules: [], fallback_size: 'M' });
        }
      } catch {
        toast.error('Failed to load size recommender config');
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allSettings = await api.get('/api/site-settings/');
      const existing = allSettings.find((s) => s.key === 'size_recommendation_rules');
      if (existing) {
        await api.put('/api/site-settings/size_recommendation_rules', { value: JSON.stringify(config) });
      } else {
        await api.post('/api/site-settings/', {
          key: 'size_recommendation_rules',
          value: JSON.stringify(config),
          is_active: true,
        });
      }
      toast.success('Size recommender rules saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addRule = () => {
    setConfig((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        {
          height_min_cm: '',
          height_max_cm: '',
          weight_min_kg: '',
          weight_max_kg: '',
          body_shapes: [],
          recommended_size: '',
        },
      ],
    }));
  };

  const updateRule = (idx, field, value) => {
    setConfig((prev) => {
      const rules = [...prev.rules];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, rules };
    });
  };

  const toggleBodyShape = (idx, shape) => {
    setConfig((prev) => {
      const rules = [...prev.rules];
      const current = rules[idx].body_shapes || [];
      rules[idx] = {
        ...rules[idx],
        body_shapes: current.includes(shape) ? current.filter((s) => s !== shape) : [...current, shape],
      };
      return { ...prev, rules };
    });
  };

  const removeRule = (idx) => {
    setConfig((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== idx),
    }));
  };

  if (loading || !config) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium mb-2">Find My Size — Recommendation Rules</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Define rules that map height + weight + body shape to a recommended size. Rules are evaluated top to bottom.
        </p>

        <div className="flex items-center gap-3 mb-6">
          <label className="text-sm font-medium text-foreground/80">Fallback Size</label>
          <input
            type="text"
            value={config.fallback_size}
            onChange={(e) => setConfig({ ...config, fallback_size: e.target.value })}
            className="w-24 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            placeholder="M"
          />
          <span className="text-xs text-muted-foreground/70">Used when no rule matches</span>
        </div>

        <div className="space-y-4">
          {config.rules.map((rule, idx) => (
            <div key={idx} className="border border-border rounded-lg p-4 bg-muted/50">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <span className="text-xs font-medium text-muted-foreground">Rule {idx + 1}</span>
                <button
                  onClick={() => removeRule(idx)}
                  className="text-red-500 hover:text-red-700 dark:hover:text-red-300 text-xs"
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Height Min (cm)</label>
                  <input
                    type="number"
                    value={rule.height_min_cm}
                    onChange={(e) => updateRule(idx, 'height_min_cm', parseFloat(e.target.value) || '')}
                    className="w-full px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Height Max (cm)</label>
                  <input
                    type="number"
                    value={rule.height_max_cm}
                    onChange={(e) => updateRule(idx, 'height_max_cm', parseFloat(e.target.value) || '')}
                    className="w-full px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Weight Min (kg)</label>
                  <input
                    type="number"
                    value={rule.weight_min_kg}
                    onChange={(e) => updateRule(idx, 'weight_min_kg', parseFloat(e.target.value) || '')}
                    className="w-full px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Weight Max (kg)</label>
                  <input
                    type="number"
                    value={rule.weight_max_kg}
                    onChange={(e) => updateRule(idx, 'weight_max_kg', parseFloat(e.target.value) || '')}
                    className="w-full px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">→ Size</label>
                  <input
                    type="text"
                    value={rule.recommended_size}
                    onChange={(e) => updateRule(idx, 'recommended_size', e.target.value)}
                    className="w-full px-2 py-1.5 border border-input rounded-lg text-sm font-medium focus:outline-none focus:border-foreground"
                    placeholder="M"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-muted-foreground mb-1">Body Shapes (leave empty for all)</label>
                <div className="flex flex-wrap gap-2">
                  {BODY_SHAPES.map((shape) => (
                    <button
                      key={shape}
                      type="button"
                      onClick={() => toggleBodyShape(idx, shape)}
                      className={`px-3 py-1 text-xs rounded-full border transition ${
                        (rule.body_shapes || []).includes(shape)
                          ? 'bg-primary text-primary-foreground border-foreground'
                          : 'bg-card text-muted-foreground border-input hover:border-gray-500'
                      }`}
                    >
                      {BODY_SHAPE_LABELS[shape]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={addRule} className="mt-4 px-4 py-2 bg-muted text-sm rounded-lg hover:bg-accent">
          + Add Rule
        </button>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Rules'}
        </button>
      </div>
    </div>
  );
};

/* ====================================================================
   TAB: CURRENCY SETTINGS
   ==================================================================== */
const CURRENCY_LABELS = {
  USD: 'US Dollar',
  CAD: 'Canadian Dollar',
  GBP: 'British Pound',
  EUR: 'Euro',
  CHF: 'Swiss Franc',
  NOK: 'Norwegian Krone',
  AED: 'UAE Dirham',
  SAR: 'Saudi Riyal',
  QAR: 'Qatari Riyal',
  KWD: 'Kuwaiti Dinar',
  INR: 'Indian Rupee',
  CNY: 'Chinese Yuan',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  HKD: 'Hong Kong Dollar',
  SGD: 'Singapore Dollar',
  MYR: 'Malaysian Ringgit',
  THB: 'Thai Baht',
  AUD: 'Australian Dollar',
  NZD: 'New Zealand Dollar',
};

const CurrencySettingsTab = ({ toast }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/exchange-rates/');
      setRates(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTriggerCron = async () => {
    setTriggering(true);
    try {
      await api.post('/api/admin/exchange-rates/trigger-cron', {});
      toast.success('Live rates fetched and saved to DB');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to trigger cron');
    } finally {
      setTriggering(false);
    }
  };

  const startEdit = (rate) => {
    setEditingCode(rate.currency_code);
    setEditValue(String(rate.rate_vs_usd));
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditValue('');
  };

  const handleSave = async (code) => {
    const value = parseFloat(editValue);
    if (!value || value <= 0) {
      toast.error('Rate must be a positive number');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/admin/exchange-rates/${code}`, { rate_vs_usd: value, is_manual_override: true });
      toast.success(`${code} rate updated and locked to manual override`);
      setEditingCode(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const handleResumeAuto = async (code) => {
    try {
      await api.post(`/api/admin/exchange-rates/${code}/resume-auto`, {});
      toast.success(`${code} will now be updated by the daily cron`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to resume auto-update');
    }
  };

  const lastUpdated =
    rates.length > 0
      ? new Date(Math.max(...rates.map((r) => new Date(r.last_updated_at).getTime()))).toLocaleString()
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-foreground">Exchange Rates</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            DB is the single source of truth. Daily cron updates all rates at 02:00 UTC.
            {lastUpdated && <span className="ml-1">Last updated: {lastUpdated}</span>}
          </p>
        </div>
        <button
          onClick={handleTriggerCron}
          disabled={triggering}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={triggering ? 'animate-spin' : ''} />
          {triggering ? 'Fetching…' : 'Fetch Live Now'}
        </button>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Currency</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Rate (per 1 USD)</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Source</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Last Updated</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground/70 text-xs">
                  Loading…
                </td>
              </tr>
            ) : (
              rates.map((rate) => (
                <tr
                  key={rate.currency_code}
                  className={rate.currency_code === 'INR' ? 'bg-amber-50 dark:bg-amber-950/50' : ''}
                >
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{rate.currency_code}</span>
                    <span className="ml-2 text-muted-foreground/70 text-xs">{CURRENCY_LABELS[rate.currency_code]}</span>
                    {rate.currency_code === 'INR' && (
                      <span className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-md">
                        base
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {editingCode === rate.currency_code ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-28 px-2 py-1 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                        autoFocus
                      />
                    ) : (
                      <span className="font-mono">{rate.rate_vs_usd}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        rate.is_manual_override
                          ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                          : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      }`}
                    >
                      {rate.is_manual_override ? 'manual' : rate.source}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {rate.last_updated_at ? new Date(rate.last_updated_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {editingCode === rate.currency_code ? (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleSave(rate.currency_code)}
                          disabled={saving}
                          className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                        >
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs px-2 py-1 border border-input rounded-md hover:bg-muted/50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => startEdit(rate)}
                          className="text-xs px-2 py-1 border border-input rounded-md hover:bg-muted/50"
                        >
                          Override
                        </button>
                        {rate.is_manual_override && (
                          <button
                            onClick={() => handleResumeAuto(rate.currency_code)}
                            className="text-xs px-2 py-1 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-300 rounded-md hover:bg-green-50 dark:hover:bg-green-950/70"
                          >
                            Resume Auto
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground/70">
        Overriding a rate locks it — the daily cron will skip it until you click "Resume Auto". INR is highlighted
        because all product prices are stored in INR and converted from it.
      </p>
    </div>
  );
};

export default SiteSettingsPage;
